import * as React from 'react';
import { Dialog, Transition } from '@headlessui/react';

export type MunicipalityOption = { id: string; name: string; disabled?: boolean; description?: string };

export type MunicipalityPickerProps = {
  isOpen: boolean;
  onClose: () => void;
  options: MunicipalityOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
};

export function MunicipalityPicker({
  isOpen,
  onClose,
  options,
  selectedId,
  onSelect,
}: MunicipalityPickerProps) {
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Filter options based on search query
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter(o => o.name.toLowerCase().includes(q)) : options;
  }, [options, query]);

  // Auto-focus input when opened
  React.useEffect(() => {
    if (isOpen) {
      setQuery('');
      // Delay to ensure dialog is fully mounted
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSelect = (id: string) => {
    onSelect(id);
    onClose();
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="fixed inset-0 z-[10000]">
        {/* Backdrop */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 dark:bg-black/60" aria-hidden="true" />
        </Transition.Child>

        {/* Full-screen panel */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="fixed inset-0 bg-white dark:bg-slate-900 flex flex-col">
            {/* Sticky header with search */}
            <div className="sticky top-0 z-10 px-4 pt-[env(safe-area-inset-top)] pb-3 border-b border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-3">
                <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Välj kommun
                </Dialog.Title>
                <button
                  onClick={onClose}
                  className="h-9 px-3 rounded-lg text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                  aria-label="Stäng"
                >
                  Stäng
                </button>
              </div>

              {/* Search input */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 dark:text-slate-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="search"
                  placeholder="Sök kommun…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-11 w-full rounded-xl ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-800 pl-10 pr-4 text-base text-slate-900 dark:text-slate-100 placeholder:text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:focus-visible:ring-emerald-400"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    className="absolute inset-y-0 right-2 my-auto rounded-md px-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    aria-label="Rensa"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Result count */}
              {query && (
                <div className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                  {filtered.length} {filtered.length === 1 ? 'kommun' : 'kommuner'}
                </div>
              )}
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
              {filtered.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-600 dark:text-slate-400">
                  Ingen kommun hittades
                </div>
              ) : (
                <div className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map((option) => {
                    const isSelected = option.id === selectedId;
                    return (
                      <button
                        key={option.id}
                        onClick={() => !option.disabled && handleSelect(option.id)}
                        disabled={option.disabled}
                        className={`w-full min-h-[48px] px-4 py-3 text-left flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 active:bg-slate-100 dark:active:bg-slate-800 transition-colors ${isSelected ? 'bg-emerald-50/50 dark:bg-emerald-900/20' : ''
                          } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className={`truncate text-base ${isSelected
                              ? 'font-medium text-slate-900 dark:text-slate-100'
                              : 'text-slate-900 dark:text-slate-100'
                            }`}>
                            {option.name}
                          </span>
                          {option.description && (
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {option.description}
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <span className="text-lg text-emerald-600 dark:text-emerald-400 ml-2" aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Safe area spacing at bottom */}
            <div style={{ height: 'env(safe-area-inset-bottom)' }} />
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
